import { RESTClient } from "@/core/api/client/rest_client"
import { getToken } from "@/utils/api"
import { withTabSpaceHeader } from "@/utils/tabSpace"
import { injectable } from "tsyringe"
import { FetchSettings } from "./fetch_settings"
import { ResponseModel } from "../generated/api/models/response_model"
import { JsonUtil } from "../generated/api/utils/json_util"
import { APIFactory } from "../generated/api/factories/api_factory"
import { HTTPMethod } from "../generated/api/enums/http_method"

type FetchClientRequestOptions = {
  useTabSpaceHeader?: boolean
  tabSpaceId?: string | null
}

@injectable()
export class FetchClient extends RESTClient {
  constructor(settings: FetchSettings) {
    super(settings)
  }

  /**
   * @deprecated - Use the TypeSafe methods (see below) takeing a generic which must be a class.
   * All the generic typed methods return the ResponseModel for better error handling and optimized payload transfers.
   */
  public async fetchAsync(
    url: string,
    options: RequestInit = {},
  ): Promise<any> {
    const token: string = getToken()
    return this.fetchWithRetry(token, url, withTabSpaceHeader(options))
  }

  public async getWithParamsAsync<T>(
    url: string,
    request: T,
  ): Promise<ResponseModel> {
    const token: string = getToken()
    const params: string = JsonUtil.serializeJson64(request)
    url += `?params=${params}`
    const res: ResponseModel = await this.fetchWithRetry(
      token,
      url,
      withTabSpaceHeader(),
      3,
    )
    return res
  }

  public async getAsync<T>(url: string): Promise<ResponseModel> {
    const token: string = getToken()
    return this.fetchWithRetry(token, url, withTabSpaceHeader(), 3)
  }

  /*
    Example on how to use POST request with a TypeSafe Class Model:
    const client: FetchClient = getService(FetchClient)
    const model = new SupplierOpportunity()
    model.num = 666

    const response: ResponseModel = await client.postAsync<SupplierOpportunity>(
      "/supplier/opportunities/toggleinterest",
      model,
    )
  */
  public async postAsync<T>(
    url: string,
    model: T,
    requestOptions: FetchClientRequestOptions = {},
  ): Promise<ResponseModel> {
    const token: string = getToken()
    return this.fetchWithRetry(
      token,
      url,
      withTabSpaceHeader(
        this.createTabSpaceRequestOptions<T>(model, HTTPMethod.POST),
        requestOptions.useTabSpaceHeader !== false,
        requestOptions.tabSpaceId,
      ),
    )
  }

  public async putAsync<T>(
    url: string,
    model: T,
    requestOptions: FetchClientRequestOptions = {},
  ): Promise<ResponseModel> {
    const token: string = getToken()
    return this.fetchWithRetry(
      token,
      url,
      withTabSpaceHeader(
        this.createTabSpaceRequestOptions<T>(model, HTTPMethod.PUT),
        requestOptions.useTabSpaceHeader !== false,
        requestOptions.tabSpaceId,
      ),
    )
  }

  public async patchAsync<T>(
    url: string,
    model: T,
    requestOptions: FetchClientRequestOptions = {},
  ): Promise<ResponseModel> {
    const token: string = getToken()
    return this.fetchWithRetry(
      token,
      url,
      withTabSpaceHeader(
        this.createTabSpaceRequestOptions<T>(model, HTTPMethod.PATCH),
        requestOptions.useTabSpaceHeader !== false,
        requestOptions.tabSpaceId,
      ),
    )
  }

  public async deleteAsync<T>(
    url: string,
    model: T,
    requestOptions: FetchClientRequestOptions = {},
  ): Promise<ResponseModel> {
    const token: string = getToken()
    return this.fetchWithRetry(
      token,
      url,
      withTabSpaceHeader(
        this.createTabSpaceRequestOptions<T>(model, HTTPMethod.DELETE),
        requestOptions.useTabSpaceHeader !== false,
        requestOptions.tabSpaceId,
      ),
    )
  }

  private createTabSpaceRequestOptions<T>(
    model: T,
    method: string,
  ): RequestInit {
    const request = APIFactory.createRequest(model)

    return {
      method,
      headers: {
        Accept: "application/json",
      },
      body: JSON.stringify(request),
    }
  }
}
